import { AppRoutes } from '@/constants/routes';
import { Permissions } from '@/constants/permissions';
import type { Guide } from './types';

/**
 * Accounts Payable — paying a supplier, from raising the voucher to reversing a posted one.
 *
 * Deliberately separate from the cheque guides: paying by cheque is one route through this process,
 * and someone paying by bank transfer should not have to read about cheque books to understand it.
 * The cheque-specific detail stays in its own section and is linked from here.
 */

export const apPaymentGuides: Guide[] = [
  // ── 1. Overview ────────────────────────────────────────────────────────────
  {
    slug: 'ap-payment-overview',
    title: 'Paying a supplier, end to end',
    section: 'ap-payments',
    level: 'Basics',
    summary:
      'The whole journey: raise the voucher, say where the money comes from, pick the bills it settles, '
      + 'get it approved, post it — and what each stage actually changes.',
    menuPath: 'Accounts Payable',
    screens: [
      { label: 'AP Payments', route: AppRoutes.AP_PAYMENTS,    permission: Permissions.AP_PAYMENT_VIEW },
      { label: 'New Payment', route: AppRoutes.AP_PAYMENT_NEW,  permission: Permissions.AP_PAYMENT_CREATE },
      { label: 'AP Bills',    route: AppRoutes.AP_BILLS,        permission: Permissions.AP_BILL_VIEW },
    ],
    blocks: [
      { kind: 'callout', tone: 'tip', title: 'Read this one first',
        text: 'Every other payments guide is a close-up of one stage below.' },

      { kind: 'para', text:
        'A supplier payment does two separate things, and it helps to keep them apart in your head. It moves '
        + 'money out of an account you own, and it settles bills the supplier has raised against you. You can do '
        + 'the first without the second — that is an on-account payment, a credit sitting on the supplier’s '
        + 'ledger waiting to be applied later.' },

      { kind: 'heading', text: 'The stages' },
      { kind: 'steps', steps: [
        { title: 'Raise the voucher',
          where: 'Accounts Payable → Payments → New',
          detail:
            'Choose the supplier, the payment date and the currency. The posting date is what decides which '
            + 'financial period the entry lands in — it does not have to equal the payment date.' },
        { title: 'Add payment lines',
          detail:
            'One line per way the money moves. A single payment can be part cheque and part bank transfer; each '
            + 'line carries its own method, its own account and its own amount.' },
        { title: 'Say which bills it settles',
          where: 'Bills Settled tab',
          detail:
            'Tick the outstanding bills. Leave it empty to pay on account. The picks are re-checked at posting '
            + 'time, because only the balance at the moment of settlement is authoritative.' },
        { title: 'Submit for approval',
          where: 'Workflow tab',
          detail:
            'Maker-checker. Whether the person who raised the voucher may approve it is a company setting — '
            + 'strict segregation of duties requires a second person.' },
        { title: 'Post it',
          detail:
            'The moment the money actually moves. Everything below happens in one transaction: if any part '
            + 'fails, none of it happens.' },
      ] },

      { kind: 'heading', text: 'What posting actually does' },
      { kind: 'list', items: [
        'Writes the GL journal — debit the supplier control account, credit the account each line draws on.',
        'Creates a credit open item on the supplier’s ledger for the payment.',
        'Settles the bills you picked, moving their balances and recognising any realised exchange difference.',
        'Registers a cheque, for any cheque line — and spends the cheque book leaf.',
        'Moves the petty cash float, for any petty cash line.',
      ] },

      { kind: 'callout', tone: 'note', title: 'Posted vouchers are never edited',
        text:
          'A posted payment is immutable. To undo it you reverse it, which creates a mirror-image entry linked '
          + 'to the original. The history of what happened stays on the record.' },
    ],
    related: ['ap-payment-funding', 'ap-payment-payee', 'ap-payment-bills', 'ap-payment-approval',
              'ap-payment-reversal'],
  },

  // ── 2. Funding — GAP-002 / 012 / 013 ───────────────────────────────────────
  {
    slug: 'ap-payment-funding',
    title: 'Choosing how the money moves',
    section: 'ap-payments',
    level: 'Basics',
    summary:
      'Payment methods, and why the “Pay from” list changes when you change the method — including cash, '
      + 'cheque and petty cash.',
    menuPath: 'Accounts Payable',
    screens: [
      { label: 'New Payment',     route: AppRoutes.AP_PAYMENT_NEW,          permission: Permissions.AP_PAYMENT_CREATE },
      { label: 'Payment Methods', route: AppRoutes.PMT_METHOD_SETUP,    permission: Permissions.PMT_METHOD_VIEW },
      { label: 'Petty Cash',      route: AppRoutes.PETTY_CASH_FLOATS,       permission: Permissions.PTY_FLOAT_VIEW },
    ],
    blocks: [
      { kind: 'para', text:
        'Every payment line answers one question: where does this money physically come from? The Payment Type '
        + 'you choose decides what kind of answer is possible, and the “Pay from” list changes to match.' },

      { kind: 'heading', text: 'What each method offers' },
      { kind: 'table',
        headers: ['Payment Type', '“Pay from” offers', 'Why'],
        rows: [
          ['Cheque, Bank Transfer, Online Banking',
           'Your bank accounts',
           'The money leaves a bank account, and posting resolves the GL account through it.'],
          ['Cash',
           'Cash on Hand GL accounts',
           'There is no bank account involved — the account IS the answer. This is the only way to reach a till.'],
          ['Petty Cash',
           'Your registered petty cash floats',
           'The money leaves a specific cash box, and that box’s balance has to move with it.'],
          ['Credit Card, Debit Card',
           'Whatever your Payment Methods setup allows',
           'Cards are often disabled for supplier payments; if so, the method offers nothing and says why.'],
        ] },

      { kind: 'callout', tone: 'note', title: 'Why an account you expected is missing',
        text:
          'The list is filtered by the method, by the voucher’s currency (a USD bank account will not appear on '
          + 'an LKR payment), and by your location. An account tied to another location is not offered here.' },

      { kind: 'callout', tone: 'warn', title: 'Changing the method clears your account choice',
        text:
          'If you pick a bank account and then switch the line to Cash, the account is cleared — it is not valid '
          + 'for the new method, and leaving it selected is how the wrong account reaches the server.' },

      { kind: 'heading', text: 'Petty cash is its own method, not a kind of cash' },
      { kind: 'para', text:
        'Choosing Petty Cash replaces “Pay from” with a cash-box picker showing each float and what it currently '
        + 'holds. Posting takes the money out of that box, and a box that does not hold enough refuses the '
        + 'posting outright — a cash tin cannot pay out money it does not physically have.' },
      { kind: 'callout', tone: 'note', title: 'A float’s account is not offered under plain Cash',
        text:
          'Once a float is registered against a GL account, that account disappears from the ordinary Cash list. '
          + 'Paying from it as generic Cash would post the ledger entry and leave the box untouched — so the box '
          + 'would hold less money than the system says it does.' },

      { kind: 'heading', text: 'The list is a convenience; the server is the control' },
      { kind: 'para', text:
        'Filtering the dropdown is there to help you. The same rules run again when you save, so a voucher that '
        + 'reaches the system another way — an import, an API call, or a stale screen — is refused just the same. '
        + 'If a save is rejected, the message names the line and says what to do about it.' },

      { kind: 'heading', text: 'The header “Default Payment Method”' },
      { kind: 'para', text:
        'The method at the top of the voucher seeds new lines — it is a starting point, not the decision. Each '
        + 'line’s own Payment Type is what moves the money. When you save, the voucher’s own method is set from '
        + 'its lines: all lines the same means that method, and a voucher paying more than one way is recorded '
        + 'as Mixed, which is what the payment list and its filter then show.' },
    ],
    related: ['ap-payment-overview', 'ap-payment-payee', 'cheque-payment', 'account-locations-refusals'],
  },

  // ── 3. Bills ───────────────────────────────────────────────────────────────
  {
    slug: 'ap-payment-bills',
    title: 'Settling bills with a payment',
    section: 'ap-payments',
    level: 'Basics',
    summary:
      'Choose which outstanding bills a payment clears, pay part of one, or pay on account and apply it later.',
    menuPath: 'Accounts Payable',
    screens: [
      { label: 'AP Payments',    route: AppRoutes.AP_PAYMENTS,   permission: Permissions.AP_PAYMENT_VIEW },
      { label: 'AP Bills',       route: AppRoutes.AP_BILLS,      permission: Permissions.AP_BILL_VIEW },
      { label: 'AP Allocations', route: AppRoutes.AP_ALLOCATION, permission: Permissions.AP_ALLOCATION_VIEW },
    ],
    blocks: [
      { kind: 'para', text:
        'The Bills Settled tab lists everything this supplier still owes you money on. Ticking a bill allocates '
        + 'the payment against it; the tick fills in the smaller of what the bill still owes and what is left of '
        + 'the payment.' },

      { kind: 'heading', text: 'Three ways to use it' },
      { kind: 'table',
        headers: ['You want to', 'Do this'],
        rows: [
          ['Pay specific bills in full',    'Tick each one. The amount fills in automatically.'],
          ['Pay part of a bill',            'Tick it, then type the amount you are actually paying.'],
          ['Clear the oldest first',        'Use the auto-allocate action — it works down from the oldest bill.'],
          ['Pay on account',                'Tick nothing. The payment becomes a credit on the supplier’s ledger.'],
        ] },

      { kind: 'callout', tone: 'note', title: 'Over-payment is allowed and is not an error',
        text:
          'If the payment is worth more than the bills you ticked, the remainder stays outstanding on the '
          + 'payment’s own open item as an on-account credit. That is exactly what an over-payment is.' },

      { kind: 'heading', text: 'Why picks are re-checked at posting' },
      { kind: 'para', text:
        'A bill can be settled by someone else between the moment you raise the voucher and the moment you post '
        + 'it. Only the outstanding balance at the moment of settlement is authoritative, so the picks are '
        + 'validated again during posting. If one no longer holds, the whole posting is refused rather than '
        + 'quietly turning your payment into an on-account credit.' },

      { kind: 'heading', text: 'Foreign-currency bills' },
      { kind: 'para', text:
        'When you settle a bill at a different exchange rate from the one it was raised at, the difference is a '
        + 'realised exchange gain or loss. It is posted automatically to the configured FX account as part of '
        + 'the settlement — you do not journal it yourself.' },
    ],
    related: ['ap-payment-overview', 'ap-payment-reversal'],
  },

  // ── 4. Approval and posting ────────────────────────────────────────────────
  {
    slug: 'ap-payment-approval',
    title: 'Approving and posting a payment',
    section: 'ap-payments',
    level: 'Basics',
    summary:
      'The workflow from draft to posted, what each status means, and the checks that can stop a posting.',
    menuPath: 'Accounts Payable',
    screens: [
      { label: 'AP Payments',       route: AppRoutes.AP_PAYMENTS,      permission: Permissions.AP_PAYMENT_VIEW },
      { label: 'Financial Periods', route: AppRoutes.MST_FINANCIAL_PERIOD, permission: Permissions.GL_PERIOD_VIEW },
    ],
    blocks: [
      { kind: 'heading', text: 'The statuses' },
      { kind: 'table',
        headers: ['Status', 'What it means', 'What you can do'],
        rows: [
          ['Draft',    'Being prepared. Nothing has moved.',              'Edit, submit, or cancel it.'],
          ['Submitted','Waiting for an approver.',                        'Approve, reject, or cancel it.'],
          ['Rejected', 'Sent back with a reason.',                        'Edit and submit again.'],
          ['Approved', 'Cleared to post. Still nothing has moved.',       'Post it.'],
          ['Posted',   'The money has moved and the ledger is written.',  'Reverse it — it can no longer be edited.'],
          ['Cancelled','Abandoned before posting.',                       'Nothing. Raise a new voucher.'],
        ] },

      { kind: 'callout', tone: 'note', title: 'Approving your own voucher',
        text:
          'Whether the person who raised a payment may also approve it is a company-level setting. A one-person '
          + 'finance team needs it on, or nothing can ever be posted. A finance department should have it off, '
          + 'so a second pair of eyes signs off.' },

      { kind: 'heading', text: 'Why a posting can be refused' },
      { kind: 'list', items: [
        'The financial period is closed. Change the posting date, or have the period reopened.',
        'The supplier has no AP control account configured.',
        'A line names no bank or cash account, or one that is no longer valid.',
        'A bank account is not linked to a GL account.',
        'A cheque number is already on the register for that bank account.',
        'A petty cash float does not hold enough to cover its line.',
        'A bill you chose to settle no longer has the balance you allocated against it.',
      ] },
      { kind: 'callout', tone: 'warn', title: 'All or nothing',
        text:
          'Posting is one transaction. If any of the checks above fails, nothing is written — no journal, no '
          + 'cheque on the register, no float movement. You will not find a half-posted payment.' },
    ],
    related: ['ap-payment-overview', 'ap-payment-reversal', 'cheque-payment'],
  },

  // ── 5. Reversal ────────────────────────────────────────────────────────────
  {
    slug: 'ap-payment-reversal',
    title: 'Cancelling and reversing a payment',
    section: 'ap-payments',
    level: 'Advanced',
    summary:
      'Undoing a payment before it posted, and undoing one after — and why those are two completely different '
      + 'operations.',
    menuPath: 'Accounts Payable',
    screens: [
      { label: 'AP Payments', route: AppRoutes.AP_PAYMENTS, permission: Permissions.AP_PAYMENT_VIEW },
    ],
    blocks: [
      { kind: 'heading', text: 'Before posting: cancel' },
      { kind: 'para', text:
        'A draft, submitted or approved payment can be cancelled. Nothing has moved yet, so there is nothing to '
        + 'unwind — the voucher is simply marked cancelled and stops being actionable.' },
      { kind: 'list', items: [
        'Any cheque numbers the voucher was holding go back into the book as available.',
        'No ledger entry exists, so none is written.',
        'The voucher stays visible, with its cancellation reason, because a numbered document that disappears '
        + 'is a gap someone will ask about.',
      ] },

      { kind: 'heading', text: 'After posting: reverse' },
      { kind: 'para', text:
        'A posted payment is immutable — its amounts, accounts and dates can never be edited. To undo it you '
        + 'create a reversal: a new voucher that is an exact mirror of the original, linked to it, posting the '
        + 'opposite entry. The original stays on the books marked as reversed.' },
      { kind: 'callout', tone: 'note', title: 'Why not just delete it?',
        text:
          'Because the payment happened. The ledger is a record of events, and an event that is edited out of '
          + 'existence takes its audit trail with it. A reversal says “this was undone, on this date, by this '
          + 'person”, which is the truth.' },

      { kind: 'heading', text: 'What a reversal touches' },
      { kind: 'table',
        headers: ['Thing', 'What happens'],
        rows: [
          ['The GL journal',        'A mirror journal is posted, linked to the original.'],
          ['The settled bills',     'Their allocations are undone and the balances go back.'],
          ['The supplier ledger',   'The credit open item created by the payment is reversed.'],
          ['A cheque on the register', 'It does not disappear — the instrument exists. Cancel or stop it on the Cheque Register.'],
        ] },
      { kind: 'callout', tone: 'warn', title: 'A reversed cheque payment still has a cheque out there',
        text:
          'Reversing the accounting does not recall a piece of paper someone is holding. If the cheque was '
          + 'printed and issued, deal with the instrument separately — stop it at the bank and record that on '
          + 'the Cheque Register.' },
    ],
    related: ['ap-payment-overview', 'ap-payment-approval', 'cheque-reports'],
  },
];

// ── 6. Cheque payee name (GAP-004) ───────────────────────────────────────────
apPaymentGuides.push({
  slug: 'ap-payment-payee',
  title: 'Who the cheque is made out to',
  section: 'ap-payments',
  level: 'Basics',
  summary:
    'Setting a default cheque payee that differs from the name in the ledger, and changing it on '
    + 'one voucher without disturbing the default.',
  menuPath: 'Accounts Payable',
  screens: [
    { label: 'New Payment',       route: AppRoutes.AP_PAYMENT_NEW, permission: Permissions.AP_PAYMENT_CREATE },
    { label: 'Suppliers',         route: AppRoutes.AP_SUPPLIERS,   permission: Permissions.MST_SUPPLIER_VIEW },
    { label: 'Chart of Accounts', route: AppRoutes.GL_COA,         permission: Permissions.GL_COA_VIEW },
  ],
  blocks: [
    { kind: 'para', text:
      'The name on a cheque is not always the name in your ledger. You may know a supplier as '
      + '“ABC Supplier” while their cheques must be made out to “ABC Holdings (Pvt) Ltd” — a '
      + 'holding company, a trading name, a factoring arrangement. A cheque made out to the wrong '
      + 'name is refused at the counter, or worse, accepted by the wrong person.' },

    { kind: 'callout', tone: 'tip', title: 'Nothing changes until you set one',
      text:
        'With no payee configured anywhere, cheques print the supplier’s own name — exactly as they '
        + 'always have. Set a payee on one supplier and only that supplier is affected.' },

    { kind: 'heading', text: 'Where the default comes from' },
    { kind: 'table',
      headers: ['Set here', 'Applies to', 'Use it when'],
      rows: [
        ['Supplier → Cheque Payee Name',
         'That supplier only',
         'The usual case. One supplier banks under a different name.'],
        ['Chart of Accounts → Posting & Workflow → Cheque Payee Name',
         'Every supplier using that payable account',
         'You run one payable account per supplier, so the account IS the party.'],
        ['Nothing set',
         '—',
         'The supplier’s own name prints, which is the behaviour you have today.'],
      ] },

    { kind: 'para', text:
      'The supplier’s own payee wins over the account’s. Suppliers usually share one trade payables '
      + 'account, so a name set there applies to everyone paying through it — useful if you keep an '
      + 'account per supplier, and a trap if you do not. The voucher tells you which of the two it '
      + 'is reading, so you never have to guess.' },

    { kind: 'callout', tone: 'warn', title: 'Check before setting it on a shared account',
      text:
        'If several suppliers point at the same payable account, a payee set on that account will '
        + 'print on all of their cheques. Set it on the supplier instead unless you genuinely mean '
        + 'every supplier on that account.' },

    { kind: 'heading', text: 'On the payment voucher' },
    { kind: 'steps', steps: [
      { title: 'Pick the supplier',
        where: 'Accounts Payable → Payments → New',
        detail:
          'Cheque Payee Name fills in with the resolved default, and a line underneath says where '
          + 'it came from — the supplier, the payable account, or just the supplier’s name.' },
      { title: 'Leave it alone for the ordinary case',
        detail:
          'Sending the default back unchanged is not an override. The voucher keeps following the '
          + 'default, so a later correction to the supplier reaches it.' },
      { title: 'Or change it for this one payment',
        detail:
          'The field warns that it has been changed and offers “Use the default” to put it back. '
          + 'Changing it needs its own permission.' },
    ] },

    { kind: 'callout', tone: 'note', title: 'An override never edits the master file',
      text:
        'Changing the payee on a voucher changes that voucher. The supplier record and the Chart of '
        + 'Accounts are untouched, and the next payment starts from the default again.' },

    { kind: 'heading', text: 'Correcting a payee everyone uses' },
    { kind: 'para', text:
      'Fix it on the supplier, once. Every draft voucher that did not deliberately override the '
      + 'payee picks up the correction — the default is read afresh when the voucher is opened and '
      + 'again when it posts, rather than frozen into the voucher when it was raised. Vouchers that '
      + 'did override keep their own name, which is the point of having overridden it.' },

    { kind: 'heading', text: 'What ends up on the cheque' },
    { kind: 'list', items: [
      'Posting the payment registers the cheque with the payee in force at that moment.',
      'The print dialog shows that name, so what you see is what the paper gets.',
      'A print run can still override the payee to CASH — a separate, higher permission, because a cheque payable to CASH can be encashed by whoever holds it.',
      'The default, the value used, and whether it was overridden are all recorded in the voucher’s audit trail.',
    ] },

    { kind: 'callout', tone: 'note', title: 'Two different overrides',
      text:
        'Changing the payee on the voucher decides who the cheque is for. Changing it at print time '
        + 'to CASH decides that it is for nobody in particular. They are granted separately, and a '
        + 'person who may do the first cannot necessarily do the second.' },
  ],
  related: ['ap-payment-funding', 'cheque-payment', 'cheque-print-run'],
});
